/**
 * tfjs-pose-piying 主入口。
 *
 * 数据流：
 *   摄像头 video ──→ MoveNet 推理 ──→ 骨骼关键点
 *                         ↓
 *   background.type ──→ Canvas 背景层（camera / 本地图片 / 本地视频）
 *                         ↓
 *   皮影贴图层 ──→ 按人数循环套装 + 骨骼锚点仿射
 *                         ↓
 *                    骨骼调试层（可选）
 *
 * 运行门控：须先选皮影；背景可选（camera 无需选文件）。
 * 换资源须先停止；停止后保留已选资源。
 */

import '@tensorflow/tfjs-backend-webgl';
import * as tf from '@tensorflow/tfjs-core';
import * as posedetection from '@tensorflow-models/pose-detection';

import {
  isFileSystemAccessSupported,
  pickImageFile,
  pickPiyingDirectory,
  pickVideoFile,
} from './asset_picker';
import {BackgroundManager} from './background';
import {Camera} from './camera';
import {PiyingManager} from './piying';
import {RendererCanvas2d} from './renderer_canvas2d';
import {
  setBackgroundTypeChangeHandler,
  setBackgroundTypeLocked,
  setupDatGui,
} from './option_panel';
import {RUNTIME, STATE, canStartRuntime} from './params';
import {setBackendAndEnvFlags} from './util';

let detector, camera, stats, backgroundManager, piyingManager;
let startInferenceTime, numInferences = 0;
let inferenceTimeSum = 0, lastPanelUpdate = 0;
let rafId;
let renderer = null;

const ui = {
  pickPiying: null,
  pickBg: null,
  start: null,
  stop: null,
  status: null,
};

/** 窗口尺寸变化时，同步 canvas 大小与骨骼坐标映射 */
function resizeCanvas() {
  if (renderer == null || camera == null) {
    return;
  }
  renderer.resize(camera.video.videoWidth, camera.video.videoHeight);
}

/** 根据 STATE.modelConfig 创建 MoveNet 检测器 */
async function createDetector() {
  let modelType;
  if (STATE.modelConfig.type == 'lightning') {
    modelType = posedetection.movenet.modelType.SINGLEPOSE_LIGHTNING;
  } else if (STATE.modelConfig.type == 'thunder') {
    modelType = posedetection.movenet.modelType.SINGLEPOSE_THUNDER;
  } else if (STATE.modelConfig.type == 'multipose') {
    modelType = posedetection.movenet.modelType.MULTIPOSE_LIGHTNING;
  }
  const modelConfig = {modelType};

  if (STATE.modelConfig.customModel !== '') {
    modelConfig.modelUrl = STATE.modelConfig.customModel;
  }
  if (STATE.modelConfig.type === 'multipose') {
    modelConfig.enableTracking = STATE.modelConfig.enableTracking;
  }
  return posedetection.createDetector(STATE.model, modelConfig);
}

/**
 * 响应 GUI 变更：重新初始化摄像头、背景、模型或后端。
 * 各模块通过 STATE.is*Changed 标志触发。
 */
async function checkGuiUpdate() {
  if (STATE.isTargetFPSChanged || STATE.isSizeOptionChanged) {
    camera = await Camera.setup(STATE.camera);
    resizeCanvas();
    STATE.isTargetFPSChanged = false;
    STATE.isSizeOptionChanged = false;
  }

  if (STATE.isBackgroundChanged) {
    try {
      await backgroundManager.update(camera.video);
    } catch (error) {
      alert(error);
    }
    STATE.isBackgroundChanged = false;
    updateRuntimeBar();
  }

  if (STATE.isModelChanged || STATE.isFlagChanged || STATE.isBackendChanged) {
    STATE.isModelChanged = true;

    window.cancelAnimationFrame(rafId);

    if (detector != null) {
      detector.dispose();
    }

    if (STATE.isFlagChanged || STATE.isBackendChanged) {
      await setBackendAndEnvFlags(STATE.flags, STATE.backend);
    }

    try {
      detector = await createDetector(STATE.model);
    } catch (error) {
      detector = null;
      alert(error);
    }

    STATE.isFlagChanged = false;
    STATE.isBackendChanged = false;
    STATE.isModelChanged = false;
  }
}

function beginEstimatePosesStats() {
  startInferenceTime = (performance || Date).now();
}

/** 统计推理耗时并更新 FPS 面板（每秒刷新一次） */
function endEstimatePosesStats() {
  const endInferenceTime = (performance || Date).now();
  inferenceTimeSum += endInferenceTime - startInferenceTime;
  ++numInferences;

  const panelUpdateMilliseconds = 1000;
  if (endInferenceTime - lastPanelUpdate >= panelUpdateMilliseconds) {
    const averageInferenceTime = inferenceTimeSum / numInferences;
    inferenceTimeSum = 0;
    numInferences = 0;
    stats.customFpsPanel.update(
        1000.0 / averageInferenceTime, 120 /* maxValue */);
    lastPanelUpdate = endInferenceTime;
  }
}

function getIdleMessage() {
  if (RUNTIME.running) {
    return null;
  }
  if (!piyingManager.isReady()) {
    return '请选择皮影文件夹后点击「开始」';
  }
  if (!backgroundManager.isReady()) {
    return '请选择背景图片/视频后点击「开始」';
  }
  return '资源已就绪，点击「开始」运行';
}

/**
 * 单帧渲染：检测 + 绘制。
 * 未运行时不推理，仅绘制背景与提示。
 */
async function renderResult() {
  if (camera.video.readyState < 2) {
    await new Promise((resolve) => {
      camera.video.onloadeddata = () => {
        resolve(video);
      };
    });
  }

  let poses = null;

  if (RUNTIME.running && detector != null) {
    beginEstimatePosesStats();
    try {
      poses = await detector.estimatePoses(
          camera.video,
          {maxPoses: STATE.modelConfig.maxPoses, flipHorizontal: false});
    } catch (error) {
      detector.dispose();
      detector = null;
      alert(error);
    }
    endEstimatePosesStats();
  }

  const backgroundSource = backgroundManager.getDrawSource(camera.video);
  const mirrorBackground = STATE.background.type === 'camera';
  const rendererParams = [
    backgroundSource,
    mirrorBackground,
    poses,
    STATE.isModelChanged,
    getIdleMessage(),
  ];
  renderer.draw(rendererParams);
}

/** requestAnimationFrame 主循环 */
async function renderPrediction() {
  await checkGuiUpdate();

  if (!STATE.isModelChanged) {
    await renderResult();
  }

  rafId = requestAnimationFrame(renderPrediction);
}

function updateRuntimeBar() {
  const running = RUNTIME.running;
  const piyingReady = piyingManager != null && piyingManager.isReady();
  const bgReady = backgroundManager != null && backgroundManager.isReady();
  const canStart = canStartRuntime(piyingReady, bgReady);
  const bgType = STATE.background.type;
  const needBgFile = bgType === 'img' || bgType === 'video';

  if (ui.pickPiying) {
    ui.pickPiying.disabled = running;
  }
  if (ui.pickBg) {
    ui.pickBg.disabled = running || !needBgFile;
    ui.pickBg.textContent = bgType === 'video' ? '选择背景视频' :
        bgType === 'img' ? '选择背景图片' : '选择背景文件';
  }
  if (ui.start) {
    ui.start.disabled = running || !canStart;
  }
  if (ui.stop) {
    ui.stop.disabled = !running;
  }

  setBackgroundTypeLocked(running);

  if (ui.status) {
    const piyingLabel = piyingReady ?
        `皮影: ${piyingManager.getPackIds().join(', ')}（${
            piyingManager.getPackCount()} 套）` :
        '皮影: 未选择';
    const bgLabel = backgroundManager.getStatusLabel();
    const runLabel = running ? '运行中' : (canStart ? '已停止 / 可开始' : '未就绪');
    ui.status.textContent = `${runLabel} ｜ ${piyingLabel} ｜ ${bgLabel}`;
  }
}

async function onPickPiying() {
  if (RUNTIME.running) {
    return;
  }
  try {
    const packs = await pickPiyingDirectory();
    await piyingManager.loadFromPacks(packs);
    updateRuntimeBar();
  } catch (error) {
    if (error && error.name === 'AbortError') {
      return;
    }
    alert(error.message || error);
  }
}

async function onPickBackground() {
  if (RUNTIME.running) {
    return;
  }
  const type = STATE.background.type;
  if (type !== 'img' && type !== 'video') {
    return;
  }
  try {
    if (type === 'img') {
      const file = await pickImageFile();
      await backgroundManager.setLocalImage(file);
    } else {
      const file = await pickVideoFile();
      await backgroundManager.setLocalVideo(file);
    }
    await backgroundManager.update(camera.video);
    updateRuntimeBar();
  } catch (error) {
    if (error && error.name === 'AbortError') {
      return;
    }
    alert(error.message || error);
  }
}

async function onStart() {
  if (RUNTIME.running) {
    return;
  }
  if (!canStartRuntime(piyingManager.isReady(), backgroundManager.isReady())) {
    alert('请先选择皮影；若背景为图片/视频，还需选择对应文件');
    return;
  }
  try {
    await backgroundManager.update(camera.video);
  } catch (error) {
    alert(error);
    return;
  }
  RUNTIME.running = true;
  updateRuntimeBar();
}

function onStop() {
  if (!RUNTIME.running) {
    return;
  }
  RUNTIME.running = false;
  backgroundManager.pauseVideo();
  updateRuntimeBar();
}

function setupRuntimeBar() {
  ui.pickPiying = document.getElementById('btn-pick-piying');
  ui.pickBg = document.getElementById('btn-pick-bg');
  ui.start = document.getElementById('btn-start');
  ui.stop = document.getElementById('btn-stop');
  ui.status = document.getElementById('runtime-status');

  ui.pickPiying.addEventListener('click', onPickPiying);
  ui.pickBg.addEventListener('click', onPickBackground);
  ui.start.addEventListener('click', onStart);
  ui.stop.addEventListener('click', onStop);

  if (!isFileSystemAccessSupported()) {
    ui.status.textContent =
        '当前浏览器不支持本地文件夹选择，请使用 Chrome / Edge';
    ui.pickPiying.disabled = true;
    ui.pickBg.disabled = true;
    ui.start.disabled = true;
  }

  updateRuntimeBar();
}

/** 初始化 Stats.js FPS 面板 */
function setupStats() {
  const stats = new Stats();
  stats.customFpsPanel = stats.addPanel(new Stats.Panel('FPS', '#0ff', '#002'));
  stats.showPanel(stats.domElement.children.length - 1);

  const parent = document.getElementById('stats');
  parent.appendChild(stats.domElement);

  const statsPanes = parent.querySelectorAll('canvas');

  for (let i = 0; i < statsPanes.length; ++i) {
    statsPanes[i].style.width = '140px';
    statsPanes[i].style.height = '80px';
  }
  return stats;
}

/** 应用启动：初始化 GUI → 摄像头 → 模型；默认不运行，待选皮影后开始 */
async function app() {
  await setupDatGui();
  setBackgroundTypeChangeHandler(() => {
    updateRuntimeBar();
  });

  stats = setupStats();

  camera = await Camera.setup(STATE.camera);
  backgroundManager = new BackgroundManager();
  await backgroundManager.update(camera.video);

  piyingManager = new PiyingManager();

  await setBackendAndEnvFlags(STATE.flags, STATE.backend);
  await tf.ready();
  detector = await createDetector();
  const canvas = document.getElementById('output');
  renderer = new RendererCanvas2d(canvas, piyingManager);
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  setupRuntimeBar();
  renderPrediction();
}

app();
